import styles from '../ScenarioGenerator.module.css';
import msStyles from '../../../common/MultiSelectDropdown.module.css';
import { Tooltip } from '../../../common/Tooltip';
import { HelpTooltip } from '../../../common/HelpTooltip';
import { ChevronIcon } from '../../../common/selectIcons';
import { VideoFrameModeSelector } from '../VideoFrameModeSelector';
import { ImageModelDropdownPortal } from '../ImageModelDropdownPortal';
import { usePortalDropdown } from '../hooks/usePortalDropdown';
import { DEFAULT_GENERATION_MODEL } from '../../state/useGenerationState';
import { gptImageFamilyDisplayName, isGptImageModelCode, stripGptPrefix } from '../../utils/imageModelFamily';
import { resolveImageResourceType, resolveImageSizeFromResolution } from '../hooks/useCreditCalculations';
import { useCreditRates } from '../../../../features/organizations/hooks';
import type { SetupPanelProps } from '../SetupPanel';
import { useScenarioSetup } from '../hooks/ScenarioSetupContext';

export function FirstFrameSettingsSection(props: SetupPanelProps) {
  const { accordions, videoGenerationMutation } = props;

  const {
    videoGenerationMode, setVideoGenerationMode,
    avoidFaces, setAvoidFaces,
    illustrationStoryboard, setIllustrationStoryboard,
    visualStyleIsStylized,
    scenarioGenerationModel, selectScenarioImageModel,
    GENERATION_MODEL_SPEED,
    autoGenerateFirstFrame, setAutoGenerateFirstFrame,
    autoGenerateStoryboard, setAutoGenerateStoryboard,
    videoSettings, imageGenerationModels } = useScenarioSetup();

  const {
    isFirstFrameSettingsOpen, setIsFirstFrameSettingsOpen } = accordions;

  // 画像生成モデル選択のポータルドロップダウン (動画モデル選択・シナリオ生成AIモデル選択と共通スタイル)
  const imageModelDropdown = usePortalDropdown({
    triggerClassName: styles.imageModelTrigger,
  });

  // 各モデルの1枚あたり消費ポイント (ドロップダウンの pt バッジ用)。ScenarioSetupContext には
  // creditRatesData が露出していないため、react-query キャッシュ (queryKey: ['credit-rates']) を
  // 直接参照する (ScenarioGenerator.tsx の useCreditRates() と同じキーで、実質追加リクエストなし)。
  const { data: creditRatesData } = useCreditRates();

  // 絵コンテ生成モデルは全動画モデル共通で Gemini 標準に統一 (2026-08-14・
  // StoryboardCandidates の STORYBOARD_MODEL と同じ規約)。
  // アコーディオンサマリはスペースなしの「Gemini標準」表記 (ユーザー指定)。
  const storyboardModelLabel = 'Gemini標準';

  // 画像生成モデルトリガーの表示内容 (ファミリー名 + ティア名 + 1枚あたり pt)。
  // 「静止画フレームから動画化」カード内のドロップダウン用。値・料率ソースは
  // useCreditCalculations と同一 (docs コメントは ImageModelDropdownPortal 参照)。
  const activeModelCode = scenarioGenerationModel || DEFAULT_GENERATION_MODEL;
  const activeModel = imageGenerationModels.find((m) => m.model_code === activeModelCode);
  const isGptModel = isGptImageModelCode(activeModelCode);
  // GPT 系は通称 (標準 / 高品質。正式名称はドロップダウンの見出しツールチップで確認できる)
  const activeFamilyLabel = isGptModel
    ? `GPT ${gptImageFamilyDisplayName(activeModelCode, imageGenerationModels)}`
    : 'Google Gemini';
  const activeTierLabel = activeModel
    ? (isGptModel ? stripGptPrefix(activeModel.display_label) : activeModel.display_label)
    : activeModelCode;
  // フレームモードのアコーディオンサマリ用モデル名。Gemini 系はファミリー名を
  // スペースなしで連結した「Gemini標準」表記 (ユーザー指定)。GPT 系は
  // display_label 自体が「GPT標準」等のプレフィックス込みなのでそのまま。
  const frameSummaryModelLabel = activeModel
    ? (isGptModel ? activeModel.display_label : `Gemini${activeModel.display_label}`)
    : activeModelCode;
  const activeImageSize = resolveImageSizeFromResolution(videoSettings.resolution);
  const activeResourceType = resolveImageResourceType(activeModelCode, activeImageSize);
  const activeRate = activeResourceType && creditRatesData?.rates
    ? creditRatesData.rates.find(
        (r) => r.service_type === 'image_generation' && r.resource_type === activeResourceType,
      )
    : undefined;
  const activePts = activeRate?.credits_per_unit ?? null;

  // ストーリーボードモードの「ロックされたトリガー」表示用。常に GENERATION_MODEL_SPEED
  // (Gemini 標準) に固定されるため、選択中モデルとは独立して算出する。
  const storyboardModel = imageGenerationModels.find((m) => m.model_code === GENERATION_MODEL_SPEED);
  const storyboardFamilyLabel = 'Google Gemini';
  const storyboardTierLabel = storyboardModel?.display_label ?? '標準';
  const storyboardResourceType = resolveImageResourceType(GENERATION_MODEL_SPEED, activeImageSize);
  const storyboardRate = storyboardResourceType && creditRatesData?.rates
    ? creditRatesData.rates.find(
        (r) => r.service_type === 'image_generation' && r.resource_type === storyboardResourceType,
      )
    : undefined;
  const storyboardPts = storyboardRate?.credits_per_unit ?? null;

  return (
    <>
        <div className={`accordion-section ${isFirstFrameSettingsOpen ? 'accordion-section-open' : ''}`}>
          <button
            type="button"
            className="accordion-header"
            onClick={() => setIsFirstFrameSettingsOpen(!isFirstFrameSettingsOpen)}
            aria-expanded={isFirstFrameSettingsOpen}
          >
            <span className="accordion-title">
              <img src="/picture_stack_landscape.svg" alt="" className="accordion-title-icon" />
              <span>参考画像</span>
              <span className="accordion-summary">
                {videoGenerationMode === 'reference'
                  ? '参考画像'
                  : videoGenerationMode === 'storyboard'
                  ? `ストーリーボード / ${storyboardModelLabel}${autoGenerateStoryboard ? ' / 画像自動生成' : ''}`
                  : (
                      <>
                        最初のフレーム
                        {' / '}
                        {frameSummaryModelLabel}
                        {autoGenerateFirstFrame && ' / フレーム自動生成'}
                      </>
                    )
                }
              </span>
            </span>
            <span className={`accordion-icon ${isFirstFrameSettingsOpen ? 'accordion-icon-open' : ''}`}>
              <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor" style={{ transition: 'transform 0.2s', transform: isFirstFrameSettingsOpen ? 'rotate(90deg)' : 'none' }}><path d="M2 0l4 4-4 4z" /></svg>
            </span>
          </button>
          <div className={`accordion-panel ${isFirstFrameSettingsOpen ? 'accordion-panel-open' : 'accordion-panel-closed'}`}>
            {/* 動画生成モード選択。frameModelControl は「静止画フレームから動画化」カードの
                中に内包する画像生成モデル選択 UI (トリガー+ドロップダウン) をここで組み立てて
                渡す (下記参照)。カード内なので disabled はモデル取得中に加え、そのカードが
                選択中でないとき (videoGenerationMode !== 'frame') も効かせる。 */}
            <VideoFrameModeSelector
              mode={videoGenerationMode}
              onModeChange={setVideoGenerationMode}
              disabled={videoGenerationMutation.isPending}
              showBorders={false}
              isLiteModel={videoSettings.videoModel === 'veo-3.1-lite'}
              isPromptMode={false}
              autoGenerateFirstFrame={autoGenerateFirstFrame}
              onAutoGenerateFirstFrameChange={setAutoGenerateFirstFrame}
              frameModelControl={(
                <div className="gen-option-group">
                  <span className="gen-option-group-label">フレーム画像生成モデル</span>
                  {/* 解像度は動画生成設定アコーディオンの 1080p/720p を裏側で流用するため、
                      シナリオ画面側では解像度セレクタは出さない (frame_generator が
                      resolution → image_size を自動マップ) */}
                  {/* 基本的に変更しない設定なので、通常の msStyles.button ではなく
                      背景・枠線なしの簡素トリガー (bareModelTrigger) で表示する。
                      imageModelTrigger は usePortalDropdown の外側クリック判定用に残す。 */}
                  <button
                    type="button"
                    className={`${styles.bareModelTrigger} ${styles.imageModelTrigger}`}
                    onClick={(e) => imageModelDropdown.open('image-model', e.currentTarget)}
                    disabled={videoGenerationMutation.isPending || videoGenerationMode !== 'frame'}
                  >
                    <span className={msStyles.buttonText}>
                      <span className={msStyles.buttonLabel}>
                        <span className={msStyles.optionLabelGroup}>
                          <span>{activeFamilyLabel} {activeTierLabel}</span>
                        </span>
                        {activePts !== null && (
                          <span className={styles.bareTriggerPts}>
                            {activePts}
                            <span className={styles.bareTriggerPtsUnit}>pt</span>
                          </span>
                        )}
                      </span>
                    </span>
                    <span className={msStyles.arrow} style={{ marginLeft: 4 }}>
                      <ChevronIcon open={imageModelDropdown.openId === 'image-model'} />
                    </span>
                  </button>
                  <ImageModelDropdownPortal
                    dropdown={imageModelDropdown}
                    models={imageGenerationModels}
                    activeModelCode={activeModelCode}
                    onSelect={selectScenarioImageModel}
                    forcedRecommendedModelCode={GENERATION_MODEL_SPEED}
                    creditRatesData={creditRatesData}
                    videoResolution={videoSettings.resolution}
                  />
                </div>
              )}
            />

            {/* ストーリーボードモード専用: 画像生成モデルは全動画モデル共通で Gemini 標準に
                固定のため、フレームモードと同じトリガー見た目のまま disabled+ロックアイコンで
                「開けないが同じ見た目」に揃える (ユーザー指定)。フレームモードの画像生成モデル
                UI は VideoFrameModeSelector の「静止画フレームから動画化」カード内に移設済みの
                ため、このブロックはストーリーボードモードのみが対象。 */}
            {videoGenerationMode === 'storyboard' && (
            <div className="form-grid-2" style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {/* ⚠️ 廃止済み機能: 「人物の顔を描かない」モード (黒いサングラス方式)。
                    動画段階でサングラスを外しきれない問題が解消できず、「絵コンテを
                    イラストで描く」に一本化されて廃止された。復活予定はない。
                    旧シナリオの generation_metadata / localStorage に avoidFaces=true が
                    残っている経路があるため、state/同期ロジックとバックエンド分岐は
                    互換のため残置している (新機能でこのモードを考慮する必要はない)。 */}
                {false && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                  <label className="form-checkbox-label">
                    <input
                      type="checkbox"
                      checked={avoidFaces}
                      onChange={(e) => {
                        const v = e.target.checked;
                        setAvoidFaces(v);
                        // 「イラストで描く」とは排他
                        if (v) setIllustrationStoryboard(false);
                      }}
                      disabled={videoGenerationMutation.isPending}
                    />
                    <span>顔を描かない</span>
                  </label>
                  <HelpTooltip ariaLabel="「顔を描かない」モードのヘルプ">
                    後ろ姿・横顔・シルエット等で人物の顔が映らない構図のみを生成します。
                    Seedance 2.0 系では実在の顔を含む素材を直接利用できないため、
                    このモデルを使う場合は有効化することをおすすめします。
                  </HelpTooltip>
                </div>
                )}

                {/* 「絵コンテをイラストで描く」: 廃止済みトグル。絵コンテは Seedance 2.5
                    ガイド推奨の線画フォーマットへ全面移行し (2026-08-14)、backend が
                    フラグに依らず常に線画 (stylized 系は当該スタイル) で生成するため
                    非表示化。state/同期ロジックは互換のため残置。 */}
                {false && (() => {
                  const isStylized = visualStyleIsStylized;
                  return (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                    <label className="form-checkbox-label" style={isStylized ? { opacity: 0.4 } : undefined}>
                      <input
                        type="checkbox"
                        checked={illustrationStoryboard && !isStylized}
                        onChange={(e) => {
                          const v = e.target.checked;
                          setIllustrationStoryboard(v);
                          // 「顔を描かない」とは排他
                          if (v) setAvoidFaces(false);
                        }}
                        disabled={videoGenerationMutation.isPending || isStylized}
                      />
                      <span>絵コンテをイラストで描く</span>
                    </label>
                    <HelpTooltip ariaLabel="「絵コンテをイラストで描く」モードのヘルプ">
                      {isStylized
                        ? '選択中の映像スタイル（非写実系）では、絵コンテも完成動画も当該スタイルで生成されるため、このオプションは使用できません。'
                        : '絵コンテを手書き風イラストで生成します。参照画像に実在の顔写真を含めないため、Seedance 2.0 系で実写画像を参照できない問題を避けられます。完成動画は実写として生成されます。'}
                    </HelpTooltip>
                  </div>
                  );
                })()}

                {/* ストーリーボード画像を自動生成: シナリオ生成完了後、完了したシナリオから順に
                    絵コンテ静止画を自動生成する (1枚 / 最初のフレームのみ / ロゴ・商品画像は
                    可能なものを On)。 */}
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                  <label className="form-checkbox-label">
                    <input
                      type="checkbox"
                      checked={autoGenerateStoryboard}
                      onChange={(e) => setAutoGenerateStoryboard(e.target.checked)}
                    />
                    <span>ストーリーボード画像を自動生成</span>
                  </label>
                  <HelpTooltip ariaLabel="「ストーリーボード画像を自動生成」モードのヘルプ">
                    シナリオの生成が完了したものから順に、絵コンテ画像を自動で生成します。
                    1 案につき 1 枚・最初のフレームのみで生成し、ロゴ・商品画像は利用できる
                    ものを自動でオンにします。すべてのシナリオの完了を待たず、生成が終わった
                    シナリオから順次始まります。
                  </HelpTooltip>
                </div>
              </div>
              <div className="gen-option-group">
                <span className="gen-option-group-label">絵コンテ画像生成モデル</span>
                {/* フレームモードのトリガーと見た目を揃える (簡素トリガー化に追従) */}
                <button
                  type="button"
                  className={`${styles.bareModelTrigger} ${styles.imageModelTrigger}`}
                  disabled
                >
                  <span className={msStyles.buttonText}>
                    <span className={msStyles.buttonLabel}>
                      <span className={msStyles.optionLabelGroup}>
                        <span>{storyboardFamilyLabel} {storyboardTierLabel}</span>
                      </span>
                      {storyboardPts !== null && (
                        <span className={styles.bareTriggerPts}>
                          {storyboardPts}
                          <span className={styles.bareTriggerPtsUnit}>pt</span>
                        </span>
                      )}
                    </span>
                  </span>
                  <span className={msStyles.arrow} style={{ marginLeft: 4 }}>
                    <img src="/lock.svg" alt="" style={{ width: '11px', height: '11px', opacity: 0.6 }} />
                  </span>
                </button>
              </div>
            </div>
            )}

            <div className={styles.accordionPanelFooter}>
              <Tooltip content="閉じる">
                <button type="button" className={styles.iconButton} onClick={() => setIsFirstFrameSettingsOpen(false)}>
                  <img src="/move_collapse_vertical.svg" alt="閉じる" style={{ width: '16px', height: '16px' }} />
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
    </>
  );
}
